import { useNavigate } from "react-router-dom";
import { useUser } from "@/store/user";
import { useState } from "react";
import { api } from "@/lib/api";
import { useStats } from "@/lib/useStats";

/**
 * Paywall — shown when a non-premium user tries to open a premium lesson/test.
 * Offers: start 7-day trial (if not used) OR subscribe (monthly/yearly).
 */
export default function Paywall({ trackId, message, onBack, productId }) {
  const navigate = useNavigate();
  const { user, isAuthed, subInfo, refreshSubscription } = useUser();
  const stats = useStats();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Resolve product: explicit prop → subInfo track mapping → conservative default.
  // Fully data-driven: any product added in the DB is picked up automatically.
  const products = subInfo?.products || [];
  const productFromTrack = products.find((p) =>
    (p.unlocks_track_ids || []).includes(trackId),
  )?.id;
  const product = productId || productFromTrack || "balagha_core";
  const productInfo = products.find((p) => p.id === product) || {};
  const trialDays = productInfo?.trial_days ?? 0;
  const trialUsed = !!subInfo?.subscription?.trial_used;
  const showTrial = trialDays > 0 && !trialUsed;
  const productIcon = productInfo?.icon || "💎";
  const productName = productInfo?.name_ar || "محتوى مدفوع";
  const defaultDesc =
    productInfo?.description_ar ||
    "هذا المحتوى ضمن اشتراكٍ مدفوع. اشترك للوصول الكامل.";

  const handleStartTrial = async () => {
    setErr("");
    if (!isAuthed) {
      navigate("/login?from=paywall");
      return;
    }
    setBusy(true);
    try {
      await api.startTrial();
      await refreshSubscription();
      window.location.reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر بدء التجربة. حاول لاحقًا.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shell" data-testid="paywall-screen">
      <div
        className="back-link"
        onClick={() => (onBack ? onBack() : navigate("/tracks"))}
        data-testid="paywall-back"
      >
        ← العودة إلى المسارات
      </div>

      <div
        className="panel"
        style={{
          textAlign: "center",
          padding: "40px 24px",
          marginTop: 24,
          border: "1px solid #c79c6a55",
          boxShadow: "0 10px 40px #c79c6a22",
        }}
      >
        <div style={{ fontSize: 56, marginBottom: 8 }}>{productIcon}</div>
        <h2 style={{ marginBottom: 10 }}>{productName}</h2>
        <p
          className="muted"
          style={{ maxWidth: 520, margin: "0 auto 20px", lineHeight: 1.8 }}
        >
          {message || defaultDesc}
        </p>

        {err && (
          <div
            data-testid="paywall-error"
            style={{ color: "#ff8b8b", marginBottom: 14, fontSize: 14 }}
          >
            {err}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          {showTrial && (
            <button
              className="btn btn-primary"
              data-testid="paywall-start-trial"
              onClick={handleStartTrial}
              disabled={busy}
              style={{
                background: "#2ec48e",
                color: "#0e1430",
                padding: "12px 24px",
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              🎁 ابدأ تجربةً مجّانيّةً ({trialDays} أيام)
            </button>
          )}
          <button
            className="btn btn-gold"
            data-testid="paywall-go-pricing"
            onClick={() =>
              navigate(`/pricing${trackId ? `?from=${trackId}` : ""}`)
            }
            style={{
              padding: "12px 24px",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            ✨ عرض خطط Premium
          </button>
        </div>

        <div className="muted" style={{ fontSize: 13, lineHeight: 1.7 }}>
          ابتداءً من <strong style={{ color: "#c79c6a" }}>29 ر.س / شهر</strong>{" "}
          — تَدفع بـ mada / Apple Pay / Visa / Mastercard.
          {!isAuthed && (
            <>
              <br />
              <span style={{ opacity: 0.85 }}>
                ستحتاج لتسجيل الدخول أوّلًا لإكمال الاشتراك.
              </span>
            </>
          )}
        </div>
      </div>

      <div
        className="panel"
        style={{ marginTop: 18, padding: 20, fontSize: 14, lineHeight: 1.9 }}
      >
        <strong style={{ color: "#c79c6a" }}>ما الذي يفتح لك Premium؟</strong>
        <ul style={{ marginTop: 10, paddingInlineStart: 18 }}>
          <li>وصولٌ كاملٌ للمسارات المدفوعة (A2 → CLS) — {stats.lessons} درسًا في المنهج.</li>
          <li>{stats.tests} اختبارًا + {stats.certificates} شهادات PDF بالعربيّة والإنجليزيّة.</li>
          <li>تقييم AI للكتابات + محادثات غير محدودة مع المعلّم الذكيّ.</li>
          <li>تحديثات مجّانيّة لكلّ المحتوى الجديد.</li>
        </ul>
      </div>
    </div>
  );
}
